import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { onSnapshot, collection, query, where, writeBatch, doc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, auth, functions } from '../../firebase';
import Layout from '../../components/ui/Layout';
import PageHeader from '../../components/ui/PageHeader';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { IconArrowLeft, IconExternalLink } from '@tabler/icons-react';
import { inputStyle } from '../contracts/contractsApi';
import { projectRef, sectionCol, updateProject, setProjectLogo, setProjectHeaderImage, removeProjectHeaderImage, fileUrl, portalUrl, PROJECT_STATUSES } from './projectsApi';
import PlanImport from './PlanImport';
import HeaderFocus from './HeaderFocus';
import { PhasesSection, DeliverablesSection, BlockersSection, DecisionsSection, DocumentsSection, ClientUsersSection, Banner, Editable } from './ProjectSections';

const SECTIONS = ['phases', 'deliverables', 'blockers', 'decisions', 'documents'];

export default function ProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(undefined);
  const [data, setData] = useState({});
  const [users, setUsers] = useState([]);
  const [activity, setActivity] = useState([]);
  const [logoUrl, setLogoUrl] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const unsubs = [
      onSnapshot(projectRef(projectId), s => setProject(s.exists() ? { id: s.id, ...s.data() } : null), e => setError(e.message)),
      ...SECTIONS.map(sec => onSnapshot(sectionCol(projectId, sec), snap => {
        setData(d => ({ ...d, [sec]: snap.docs.map(x => ({ id: x.id, ...x.data() })) }));
      }, e => setError(e.message))),
      onSnapshot(query(collection(db, 'clientUsers'), where('projectIds', 'array-contains', projectId)),
        snap => setUsers(snap.docs.map(x => ({ id: x.id, ...x.data() }))), e => setError(e.message)),
      onSnapshot(query(collection(db, 'hqNotifications'), where('projectId', '==', projectId), where('readAt', '==', null)),
        snap => setActivity(snap.docs.map(x => ({ id: x.id, ...x.data() })).sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0))),
        e => setError(e.message)),
    ];
    return () => unsubs.forEach(u => u());
  }, [projectId]);

  useEffect(() => {
    if (project?.clientLogoPath) fileUrl(project.clientLogoPath).then(setLogoUrl).catch(() => setLogoUrl(null));
    else setLogoUrl(null);
  }, [project?.clientLogoPath]);

  const [headerUrl, setHeaderUrl] = useState(null);
  const [focusing, setFocusing] = useState(false);
  useEffect(() => {
    if (project?.headerImagePath) fileUrl(project.headerImagePath).then(setHeaderUrl).catch(() => setHeaderUrl(null));
    else setHeaderUrl(null);
  }, [project?.headerImagePath]);

  const back = <Button variant="ghost" icon={<IconArrowLeft size={14} stroke={1.5} />} onClick={() => navigate('/projects')}>Projects</Button>;
  if (project === undefined) return <Layout><PageHeader title="Project" subtitle={error || 'Loading...'} actions={back} /></Layout>;
  if (project === null) return <Layout><PageHeader title="Project not found" actions={back} /></Layout>;

  const run = async (fn) => {
    setBusy(true); setError(null);
    try { await fn(); } catch (e) { setError(e.message); }
    setBusy(false);
  };
  const togglePortal = () => {
    const enabled = !project.portalEnabled;
    if (enabled && !window.confirm(`Open the portal for ${project.clientName}? Invited client users will be able to see this project.`)) return;
    run(() => httpsCallable(functions, 'setPortalEnabled')({ projectId, enabled }));
  };
  const common = { projectId, project, data, users, onError: setError };

  return (
    <Layout>
      <PageHeader title={project.name} subtitle={`${project.clientName} · ${projectId}`} actions={back} />
      <div style={{ padding: 28, display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 1150 }}>
        {error && <Banner tone="danger" onClose={() => setError(null)}>{error}</Banner>}
        {activity.length > 0 && (
          <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 6 }}>
              <div style={{ fontSize: 14 }}>New client activity on Needs you ({activity.length})</div>
              <Button size="sm" variant="secondary" onClick={() => run(async () => {
                const b = writeBatch(db);
                activity.forEach(a => b.update(doc(db, 'hqNotifications', a.id), { readAt: serverTimestamp(), readByUid: auth.currentUser.uid }));
                await b.commit();
              })}>Mark as seen</Button>
            </div>
            {activity.map(a => (
              <div key={a.id} style={{ fontSize: 12, padding: '4px 0', borderTop: '0.5px solid var(--color-border-light)' }}>
                <strong style={{ fontWeight: 500 }}>{a.byName}</strong> {a.kind === 'done' ? 'marked as done' : a.kind === 'upload' ? 'uploaded to' : 'replied to'} <strong style={{ fontWeight: 500 }}>{a.blockerTitle}</strong>
                <span style={{ color: 'var(--color-text-tertiary)' }}> · {a.createdAt?.toDate?.().toLocaleString('en-GB') || 'just now'}</span>
                {a.text && <div style={{ color: 'var(--color-text-secondary)', whiteSpace: 'pre-wrap' }}>{a.text}</div>}
                {a.files?.length > 0 && <div style={{ color: 'var(--color-text-secondary)' }}>Files: {a.files.join(', ')}</div>}
              </div>
            ))}
          </Card>
        )}
        {project.paymentReceived && !project.portalEnabled && (
          <Banner tone="warning">The contract's first payment has been received. Add a phase, then open the portal.</Banner>
        )}

        <Card>
          <div style={{ display: 'flex', gap: 18, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ width: 72, height: 72, borderRadius: 'var(--radius-md)', background: 'var(--color-bg-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
              {logoUrl ? <img src={logoUrl} alt="" style={{ maxWidth: '100%', maxHeight: '100%' }} /> : <span style={{ fontSize: 10, color: 'var(--color-text-tertiary)' }}>No logo</span>}
            </div>
            <div style={{ flex: 1, minWidth: 280, display: 'grid', gridTemplateColumns: '110px 1fr', gap: '8px 12px', alignItems: 'center', fontSize: 12 }}>
              <span style={{ color: 'var(--color-text-tertiary)' }}>Project</span>
              <Editable value={project.name} onSave={v => run(() => updateProject(projectId, { name: v }))} />
              <span style={{ color: 'var(--color-text-tertiary)' }}>Client</span>
              <Editable value={project.clientName} onSave={v => run(() => updateProject(projectId, { clientName: v }))} />
              <span style={{ color: 'var(--color-text-tertiary)' }}>Status</span>
              <select style={{ ...inputStyle, width: 160, height: 28, fontSize: 12 }} value={project.status} onChange={e => run(() => updateProject(projectId, { status: e.target.value }))}>
                {PROJECT_STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <span style={{ color: 'var(--color-text-tertiary)' }}>Client logo</span>
              <input type="file" accept="image/*" style={{ fontSize: 11 }} onChange={e => e.target.files[0] && run(() => setProjectLogo(projectId, e.target.files[0], project.clientLogoPath))} />
              <span style={{ color: 'var(--color-text-tertiary)' }}>Portal header image</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <div style={{ width: 120, height: 40, borderRadius: 'var(--radius-md)', background: '#11171E', overflow: 'hidden', position: 'relative', flexShrink: 0 }}>
                  {headerUrl && <img src={headerUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: project.headerImageFocus ? `${project.headerImageFocus.x}% ${project.headerImageFocus.y}%` : 'center 40%' }} />}
                  <div style={{ position: 'absolute', inset: 0, background: 'rgba(17,23,30,0.85)', color: '#fff', fontSize: 9, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{headerUrl ? 'As clients see it' : 'None: plain dark'}</div>
                </div>
                <label style={{ fontSize: 11, color: 'var(--color-brand)', cursor: 'pointer' }}>
                  {headerUrl ? 'Replace' : 'Upload'}
                  <input type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }} onChange={e => { const f = e.target.files[0]; e.target.value = ''; if (f) run(() => setProjectHeaderImage(projectId, f, project.headerImagePath)); }} />
                </label>
                {headerUrl && <span style={{ fontSize: 11, color: 'var(--color-brand)', cursor: 'pointer' }} onClick={() => setFocusing(true)}>Adjust position</span>}
                {project.headerImagePath && <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)', cursor: 'pointer' }} onClick={() => window.confirm('Remove the portal header image? The header goes back to plain dark.') && run(() => removeProjectHeaderImage(projectId, project.headerImagePath))}>Remove</span>}
              </div>
            </div>
            <div style={{ minWidth: 220 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', opacity: busy ? 0.5 : 1 }}>
                <input type="checkbox" checked={!!project.portalEnabled} disabled={busy} onChange={togglePortal} />
                Portal {project.portalEnabled ? 'open' : 'closed'}
              </label>
              <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', margin: '4px 0 8px', lineHeight: 1.5 }}>
                {project.portalEnabled ? `Open since ${project.unlockedAt?.toDate?.().toLocaleDateString('en-GB') || 'now'}.` : 'Open it once the contract is signed and the first payment is in. Clients see a holding page until then.'}
              </div>
              {project.contractId && (
                <div style={{ fontSize: 12, marginBottom: 6 }}>
                  <span style={{ color: 'var(--color-brand)', cursor: 'pointer' }} onClick={() => navigate(`/contracts/${project.contractId}`)}>View signed contract</span>
                </div>
              )}
              <a href={portalUrl(projectId)} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: 'var(--color-brand)', display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                Preview as client <IconExternalLink size={12} stroke={1.5} />
              </a>
            </div>
          </div>
        </Card>

        {focusing && headerUrl && (
          <HeaderFocus url={headerUrl} value={project.headerImageFocus} onCancel={() => setFocusing(false)}
            onSave={(focus) => run(async () => { await updateProject(projectId, { headerImageFocus: focus }); setFocusing(false); })} />
        )}
        <PlanImport projectId={projectId} />
        <PhasesSection {...common} />
        <DeliverablesSection {...common} />
        <BlockersSection {...common} />
        <DecisionsSection {...common} />
        <DocumentsSection {...common} />
        <ClientUsersSection {...common} />
      </div>
    </Layout>
  );
}
